import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import { Helmet } from "react-helmet-async";
import api from "../hooks/useApi";

const SERVICES = [
  {
    icon: "🏠",
    title: "Residential Construction",
    desc: "Complete home building — 1BHK to 4BHK — tailored to your vision and budget.",
  },
  {
    icon: "🔧",
    title: "Home Renovation",
    desc: "Transform your existing home. Kitchen, bathroom, full-home remodel.",
  },
  {
    icon: "🎨",
    title: "Painting & Finishing",
    desc: "Premium interior & exterior painting with lasting, polished finishes.",
  },
  {
    icon: "🧱",
    title: "Lenting & Plastering",
    desc: "Expert lenting and plaster work for smooth, durable walls.",
  },
  {
    icon: "💧",
    title: "Borwell Drilling",
    desc: "Professional borwell drilling with site assessment for reliable groundwater.",
  },
  {
    icon: "🏛️",
    title: "Government Contracts",
    desc: "Certified for government infrastructure. Fast, compliant, quality delivery.",
  },
];

function CountUp({ end, suffix = "" }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  useEffect(() => {
    if (!inView) return;
    let start = 0;
    const step = end / 50;
    const timer = setInterval(() => {
      start += step;
      if (start >= end) {
        setCount(end);
        clearInterval(timer);
      } else setCount(Math.floor(start));
    }, 30);
    return () => clearInterval(timer);
  }, [inView, end]);
  return (
    <span ref={ref}>
      {count}
      {suffix}
    </span>
  );
}

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
  },
};
const stagger = { visible: { transition: { staggerChildren: 0.12 } } };

export default function Home() {
  const [projects, setProjects] = useState([]);

  // inside useEffect → fetchProjects

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const res = await api.getProjects({ featured: true });

        console.log("HOME PROJECTS:", res);

        // ✅ FIXED DATA HANDLING (only change)
        const data = Array.isArray(res.data)
          ? res.data
          : res.data?.data || res.data?.projects || [];

        if (data.length > 0) {
          setProjects(data.slice(0, 3));
        } else {
          console.warn("No featured projects found");
          setProjects([]);
        }
      } catch (err) {
        console.error("HOME PROJECT ERROR:", err?.response || err.message);
        setProjects([]);
      }
    };

    fetchProjects();
  }, []);

  return (
    <>
      <Helmet>
        <title>NirmalRam Construction — Dream Homes in Nandurbar</title>
        <meta
          name="description"
          content="NirmalRam Construction builds dream homes, handles renovation, borwell, painting, and government contracts in Nandurbar, Maharashtra. 10+ homes built since 2022."
        />
        <meta
          name="keywords"
          content="construction Nandurbar, home builder Maharashtra, NirmalRam Construction, Hitesh Patil, Rambhau Patil"
        />
      </Helmet>

      {/* ===== HERO ===== */}
      <section
        style={{
          position: "relative",
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          overflow: "hidden",
        }}
      >
        {/* Background image placeholder — replace src with real image */}
        <div
          style={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(135deg, #050505 0%, #0E0A03 40%, #050505 100%)",
          }}
        />
        {/* Decorative grid */}
        <div className="grid-bg" />
        {/* Gold accent line */}
        <div
          style={{
            position: "absolute",
            left: 0,
            top: 0,
            bottom: 0,
            width: "3px",
            background:
              "linear-gradient(to bottom, transparent, #C9A84C 30%, #C9A84C 70%, transparent)",
          }}
        />

        <div
          className="container"
          style={{ position: "relative", zIndex: 2, paddingTop: "4rem" }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr auto",
              gap: "2rem",
              alignItems: "center",
            }}
          >
            <motion.div initial="hidden" animate="visible" variants={stagger}>
              <motion.div variants={fadeUp}>
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "0.5rem",
                    background: "rgba(201,168,76,0.08)",
                    border: "1px solid rgba(201,168,76,0.25)",
                    padding: "0.4rem 1rem",
                    borderRadius: "2px",
                    fontSize: "0.72rem",
                    letterSpacing: "3px",
                    textTransform: "uppercase",
                    color: "#C9A84C",
                    marginBottom: "2rem",
                  }}
                >
                  ✦ Est. 2022 · Nandurbar, Maharashtra
                </span>
              </motion.div>
              <motion.h1
                variants={fadeUp}
                style={{
                  fontFamily: "Cormorant Garamond,serif",
                  marginBottom: "1.5rem",
                  color: "#F0EAE0",
                }}
              >
                We Build
                <br />
                <span style={{ color: "#C9A84C", fontStyle: "italic" }}>
                  Dream Homes
                </span>
                <br />
                With Precision
              </motion.h1>
              <motion.p
                variants={fadeUp}
                style={{
                  color: "#8A8070",
                  fontSize: "1.1rem",
                  lineHeight: 1.8,
                  maxWidth: "560px",
                  marginBottom: "2.5rem",
                }}
              >
                From small renovations to grand residences — NirmalRam
                Construction delivers quality craftsmanship, trusted materials,
                and on-time completion across every project in Nandurbar and
                beyond.
              </motion.p>
              <motion.div
                variants={fadeUp}
                style={{ display: "flex", gap: "1rem", flexWrap: "wrap" }}
              >
                <Link to="/projects" className="btn-gold">
                  View Our Work →
                </Link>
                <Link to="/contact" className="btn-outline">
                  Free Consultation
                </Link>
              </motion.div>
            </motion.div>

            {/* Stats panel */}
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              style={{ display: "flex", flexDirection: "column", gap: "1rem" }}
              className="hero-stats-panel"
            >
              {[
                { num: 10, suffix: "+", label: "Dream Homes" },
                { num: 5, suffix: "+", label: "Years Exp." },
                { num: 100, suffix: "%", label: "Satisfaction" },
              ].map((s) => (
                <div
                  key={s.label}
                  style={{
                    background: "rgba(22,22,22,0.9)",
                    border: "1px solid rgba(201,168,76,0.18)",
                    padding: "1.5rem 1.25rem",
                    textAlign: "center",
                    minWidth: "120px",
                    backdropFilter: "blur(10px)",
                  }}
                >
                  <span
                    style={{
                      fontFamily: "Cormorant Garamond,serif",
                      fontSize: "2.4rem",
                      color: "#C9A84C",
                      display: "block",
                    }}
                  >
                    <CountUp end={s.num} suffix={s.suffix} />
                  </span>
                  <span
                    style={{
                      fontSize: "0.68rem",
                      letterSpacing: "2px",
                      textTransform: "uppercase",
                      color: "#5A5450",
                      marginTop: "0.25rem",
                      display: "block",
                    }}
                  >
                    {s.label}
                  </span>
                </div>
              ))}
            </motion.div>
          </div>
        </div>

        {/* Scroll cue */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5 }}
          style={{
            position: "absolute",
            bottom: "2rem",
            left: "50%",
            transform: "translateX(-50%)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "0.5rem",
          }}
        >
          <span
            style={{
              fontSize: "0.65rem",
              letterSpacing: "2px",
              color: "#5A5450",
              textTransform: "uppercase",
            }}
          >
            Scroll
          </span>
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ repeat: Infinity, duration: 1.5 }}
            style={{
              width: "1px",
              height: "40px",
              background: "linear-gradient(to bottom, #C9A84C, transparent)",
            }}
          />
        </motion.div>
      </section>

      {/* ===== ABOUT STRIP ===== */}
      <section
        style={{
          background: "#0A0A0A",
          borderTop: "1px solid rgba(201,168,76,0.1)",
          borderBottom: "1px solid rgba(201,168,76,0.1)",
          padding: "3rem 0",
        }}
      >
        <div className="container">
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))",
              gap: "2rem",
              textAlign: "center",
            }}
          >
            {[
              { num: 10, suf: "+", label: "Dream Homes Built" },
              { num: 5, suf: "+", label: "Years Experience" },
              { num: 3, suf: "", label: "Govt. Contracts" },
              { num: 100, suf: "%", label: "On-Time Delivery" },
            ].map((s) => (
              <div key={s.label}>
                <div
                  style={{
                    fontFamily: "Cormorant Garamond,serif",
                    fontSize: "2.8rem",
                    color: "#C9A84C",
                  }}
                >
                  <CountUp end={s.num} suffix={s.suf} />
                </div>
                <div
                  style={{
                    fontSize: "0.8rem",
                    color: "#5A5450",
                    letterSpacing: "1.5px",
                    textTransform: "uppercase",
                    marginTop: "0.25rem",
                  }}
                >
                  {s.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== SERVICES ===== */}
      <section className="section">
        <div className="container">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            style={{ marginBottom: "3rem" }}
          >
            <span className="section-tag">What We Build</span>
            <h2>
              Full Spectrum
              <br />
              Construction Services
            </h2>
            <div className="section-divider" />
            <p style={{ color: "#8A8070", maxWidth: "520px", lineHeight: 1.8 }}>
              Every project, every size — handled with expert skill, quality
              materials, and dedication to your vision.
            </p>
          </motion.div>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit,minmax(260px,1fr))",
              gap: "1.5rem",
            }}
          >
            {SERVICES.map((s, i) => (
              <motion.div
                key={i}
                variants={fadeUp}
                style={{
                  background: "#111",
                  border: "1px solid rgba(201,168,76,0.12)",
                  padding: "2rem",
                  borderRadius: "3px",
                  transition: "all 0.3s",
                  cursor: "default",
                  position: "relative",
                  overflow: "hidden",
                }}
                whileHover={{ borderColor: "rgba(201,168,76,0.4)", y: -4 }}
              >
                <div
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    height: "2px",
                    background:
                      "linear-gradient(90deg, transparent, #C9A84C, transparent)",
                    opacity: 0,
                    transition: "0.3s",
                  }}
                  className="card-top-line"
                />
                <div
                  style={{
                    width: "52px",
                    height: "52px",
                    background: "rgba(201,168,76,0.08)",
                    border: "1px solid rgba(201,168,76,0.2)",
                    borderRadius: "3px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "1.5rem",
                    marginBottom: "1.25rem",
                  }}
                >
                  {s.icon}
                </div>
                <h3 style={{ fontSize: "1.15rem", marginBottom: "0.75rem" }}>
                  {s.title}
                </h3>
                <p
                  style={{
                    color: "#8A8070",
                    fontSize: "0.9rem",
                    lineHeight: 1.7,
                  }}
                >
                  {s.desc}
                </p>
              </motion.div>
            ))}
          </motion.div>
          <div style={{ textAlign: "center", marginTop: "3rem" }}>
            <Link to="/services" className="btn-outline">
              Explore All Services →
            </Link>
          </div>
        </div>
      </section>

      {/* ===== FEATURED PROJECTS ===== */}
      <section className="section" style={{ background: "#0A0A0A" }}>
        <div className="container">
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-end",
              marginBottom: "3rem",
              flexWrap: "wrap",
              gap: "1rem",
            }}
          >
            <div>
              <span className="section-tag">Our Portfolio</span>
              <h2>Featured Projects</h2>
              <div className="section-divider" />
            </div>
            <Link to="/projects" className="btn-ghost">
              View All Projects →
            </Link>
          </div>
          {projects.length === 0 ? (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill,minmax(300px,1fr))",
                gap: "1.5rem",
              }}
            >
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  style={{
                    background: "#111",
                    border: "1px solid rgba(201,168,76,0.1)",
                    borderRadius: "3px",
                    overflow: "hidden",
                  }}
                >
                  <div className="skeleton" style={{ height: "200px" }} />
                  <div style={{ padding: "1.25rem" }}>
                    <div
                      className="skeleton"
                      style={{ height: "20px", marginBottom: "0.75rem" }}
                    />
                    <div
                      className="skeleton"
                      style={{ height: "14px", width: "70%" }}
                    />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill,minmax(300px,1fr))",
                gap: "1.5rem",
              }}
            >
              {projects.map((p) => (
                <Link
                  key={p._id}
                  to={`/projects/${p._id}`}
                  style={{ textDecoration: "none" }}
                >
                  <motion.div
                    whileHover={{ y: -4 }}
                    style={{
                      background: "#111",
                      border: "1px solid rgba(201,168,76,0.12)",
                      borderRadius: "3px",
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        height: "200px",
                        background: "#1a1208",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "3rem",
                        position: "relative",
                      }}
                    >
                      {p.images?.[0] ? (
                        <img
                          src={p.images[0].url}
                          alt={p.title}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            position: "absolute",
                            inset: 0,
                          }}
                        />
                      ) : (
                        "🏠"
                      )}
                      <span
                        className="badge badge-gold"
                        style={{
                          position: "absolute",
                          top: "0.75rem",
                          right: "0.75rem",
                        }}
                      >
                        {p.status}
                      </span>
                    </div>
                    <div style={{ padding: "1.25rem" }}>
                      <h3
                        style={{
                          fontSize: "1.1rem",
                          marginBottom: "0.5rem",
                          color: "#F0EAE0",
                        }}
                      >
                        {p.title}
                      </h3>
                      <p style={{ color: "#5A5450", fontSize: "0.82rem" }}>
                        📍 {p.location} · {p.year}
                      </p>
                    </div>
                  </motion.div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ===== WHY CHOOSE US ===== */}
      <section className="section">
        <div className="container">
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "4rem",
              alignItems: "center",
            }}
          >
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
            >
              <span className="section-tag">Why NirmalRam</span>
              <h2>
                Built on Trust,
                <br />
                Raised on Skill
              </h2>
              <div className="section-divider" />
              <p
                style={{
                  color: "#8A8070",
                  lineHeight: 1.9,
                  marginBottom: "1.5rem",
                }}
              >
                Founded by{" "}
                <strong style={{ color: "#C9A84C" }}>Rambhau Patil</strong>, a
                retired professor with a passion for quality, and led by{" "}
                <strong style={{ color: "#C9A84C" }}>Hitesh Patil</strong>{" "}
                (B.Tech Civil Engineering, 2021) — we bring academic discipline
                and modern engineering to every build.
              </p>
              {[
                "Transparent pricing with market-rate materials",
                "Highly skilled & fast-working teams",
                "Government contract certified",
                "Free consultation & site visit",
                "Quality materials recommended per your budget",
              ].map((pt) => (
                <div
                  key={pt}
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: "0.75rem",
                    marginBottom: "0.85rem",
                  }}
                >
                  <span
                    style={{
                      color: "#C9A84C",
                      fontSize: "1rem",
                      marginTop: "2px",
                    }}
                  >
                    ✓
                  </span>
                  <span style={{ color: "#8A8070", fontSize: "0.95rem" }}>
                    {pt}
                  </span>
                </div>
              ))}
              <Link
                to="/about"
                className="btn-gold"
                style={{ marginTop: "1.5rem", display: "inline-flex" }}
              >
                Meet Our Team →
              </Link>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <div
                style={{
                  background: "#0E0E0E",
                  border: "1px solid rgba(201,168,76,0.15)",
                  padding: "2.5rem",
                  borderRadius: "3px",
                }}
              >
                <div
                  style={{
                    fontFamily: "Cormorant Garamond,serif",
                    fontSize: "4rem",
                    color: "rgba(201,168,76,0.15)",
                    lineHeight: 1,
                    marginBottom: "1rem",
                  }}
                >
                  "
                </div>
                <p
                  style={{
                    fontFamily: "Cormorant Garamond,serif",
                    fontSize: "1.3rem",
                    color: "#C9A84C",
                    lineHeight: 1.6,
                    marginBottom: "1.5rem",
                    fontStyle: "italic",
                  }}
                >
                  We don't just build houses — we build homes people are proud
                  of for generations.
                </p>
                <div
                  style={{ display: "flex", alignItems: "center", gap: "1rem" }}
                >
                  <div
                    style={{
                      width: "48px",
                      height: "48px",
                      borderRadius: "50%",
                      background: "rgba(201,168,76,0.15)",
                      border: "1px solid #C9A84C",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontFamily: "Cormorant Garamond,serif",
                      fontSize: "1.2rem",
                      color: "#C9A84C",
                    }}
                  >
                    RP
                  </div>
                  <div>
                    <div style={{ fontWeight: 500, color: "#F0EAE0" }}>
                      Rambhau Patil
                    </div>
                    <div style={{ fontSize: "0.8rem", color: "#5A5450" }}>
                      Founder, NirmalRam Construction
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ===== CTA ===== */}
      <section
        style={{
          background: "linear-gradient(135deg, #0A0800, #1a1208, #0A0800)",
          borderTop: "1px solid rgba(201,168,76,0.2)",
          borderBottom: "1px solid rgba(201,168,76,0.2)",
          padding: "5rem 0",
        }}
      >
        <div className="container" style={{ textAlign: "center" }}>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
          >
            <span className="section-tag">Start Today</span>
            <h2 style={{ marginBottom: "1rem" }}>
              Ready to Build Your
              <br />
              <span style={{ color: "#C9A84C", fontStyle: "italic" }}>
                Dream Home?
              </span>
            </h2>
            <p
              style={{
                color: "#8A8070",
                maxWidth: "480px",
                margin: "0 auto 2.5rem",
                lineHeight: 1.8,
              }}
            >
              Get a free consultation and cost estimate from our expert team.
              We'll recommend the best materials within your budget.
            </p>
            <div
              style={{
                display: "flex",
                gap: "1rem",
                justifyContent: "center",
                flexWrap: "wrap",
              }}
            >
              <Link to="/contact" className="btn-gold">
                Get Free Quote →
              </Link>
              <a href="tel:+919403646276" className="btn-outline">
                📞 Call Us Now
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <style>{`
        @media (max-width: 768px) {
          .hero-stats-panel { display: none !important; }
          section > .container > div[style*="grid-template-columns: 1fr 1fr"] { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </>
  );
}
